import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    BookOpen,
    Award,
    Users,
    Compass,
    TrendingUp,
    Lock,
    Unlock,
    RefreshCw,
    LogOut,
    ChevronRight,
    CheckCircle,
    Image as ImageIcon
} from 'lucide-react';
import Swal from 'sweetalert2';
import './OnboardingRecrue.css';
import { fetchLearnerModulesRequest } from '../../../redux/actions/learnerModulesActions';
import { setSelectedRole } from '../../../redux/actions/roleActions';
import { API_BASE_URL } from '../../../../api';
import defaultProfileImage from '../../../images/profile.jpg';
import OnboardingModulePlayer from './OnboardingModulePlayer';

const normalizeAppPath = (path) => {
    if (!path || typeof path !== "string") return "";
    const trimmed = path.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const getRolePath = (role) => {
    const roleName = `${role?.name || ''} ${role?.code || ''}`.toUpperCase();
    const compactRoleName = roleName.replace(/\s+/g, '');
    if (Number(role?.id) === 54 || compactRoleName.includes('RESPONSABLEONBOARDING')) return '/recrutement/onboarding/configuration';
    if (Number(role?.id) === 53 || compactRoleName.includes('NRONBOARDING')) return '/collaborateur/onboarding';
    const explicitPath = normalizeAppPath(role?.routePath || role?.modulePath || role?.module_path);
    if (explicitPath) return explicitPath;
    return '';
};

const OnboardingRecrue = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state?.user?.currentUser || state.auth?.user);
    const authUser = useSelector(state => state.auth?.user);
    const selectedRole = useSelector(state => state.role?.selectedRole);
    const { items = [], loading = false } = useSelector((state) => state.learnerModules || {});
    
    const [activeStep, setActiveStep] = useState(0);
    const [selectedStep, setSelectedStep] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modules, setModules] = useState([]);

    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const userDropdownRef = React.useRef(null);

    const profilePicUrl = authUser?.profilePictureUrl || currentUser?.profilePictureUrl;
    const profilePhotoSrc = profilePicUrl
        ? `${API_BASE_URL}/api/users/images/profile/${profilePicUrl}`
        : defaultProfileImage;

    const allRoles = currentUser?.roles || [];
    const userRoles = allRoles.filter(
        role => role.moduleId === selectedRole?.moduleId
    );

    const roleIcons = {
        'SUPER_ADMIN': '👑', 'ADMIN': '👔', 'FORMATION_RESP': '📚',
        'SKILLS_MANAGER': '🎯', 'HR_MANAGER': '👨‍💼', 'EMPLOYEE': '👤',
        'Responsable RH': '👨‍💼', 'Collaborateur': '👤', 'Manager': '👔',
        'Admin': '⚙️', 'Meta admin': '👑', 'DEFAULT': '👤',
    };
    const getRoleIcon = (roleName) => roleIcons[roleName] || roleIcons['DEFAULT'];

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target))
                setIsUserDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleRoleChange = (role) => {
        dispatch(setSelectedRole(role));
        const route = getRolePath(role);
        if (route) {
            navigate(route);
        }
        setIsUserDropdownOpen(false);
    };

    // Initial progression loaded from localStorage
    useEffect(() => {
        const savedProgress = localStorage.getItem(`onb_progress_${currentUser?.id || 'guest'}`);
        if (savedProgress) {
            setActiveStep(parseInt(savedProgress, 10));
        }
    }, [currentUser]);

    // Fetch assigned modules from Redux
    useEffect(() => {
        dispatch(fetchLearnerModulesRequest());
    }, [dispatch]);

    // Update steps when items are fetched
    useEffect(() => {
        const onboardingModules = (items || []).filter(m => m.type === 'ONBOARDING');
        onboardingModules.sort((a, b) => (a.id || 0) - (b.id || 0));
        
        const dynamicSteps = onboardingModules.map((m, index) => {
            const progress = m.progress || {};
            const isStepCompleted = String(progress.status || "").toUpperCase() === "COMPLETED" || progress.progressPercent === 100;
            return {
                id: index,
                moduleId: m.id,
                title: m.nom,
                desc: m.description || "Aucune description.",
                picture: m.picture,
                progress: progress,
                isCompleted: isStepCompleted,
                content: {
                    subtitle: m.nom,
                    paragraphs: [m.description || "Découvrez ce module d'intégration."],
                    duration: "À compléter"
                }
            };
        });
        setModules(dynamicSteps);

        // Calculate dynamic active step based on backend completion
        if (dynamicSteps.length > 0) {
            const firstIncompleteIndex = dynamicSteps.findIndex(step => !step.isCompleted);
            if (firstIncompleteIndex === -1) {
                // All steps are completed!
                setActiveStep(dynamicSteps.length);
            } else {
                setActiveStep(firstIncompleteIndex);
            }
        }
    }, [items]);

    const steps = modules.length > 0 ? modules : [];

    const handleStepClick = (step) => {
        if (step.id > activeStep) {
            Swal.fire({
                icon: 'lock',
                title: 'Étape verrouillée',
                text: `Veuillez compléter les étapes précédentes pour débloquer "${step.title}".`,
                confirmButtonColor: '#7c3aed'
            });
            return;
        }
        // Open the immersive module player
        setSelectedStep(step);
        setShowModal(true);
    };

    const handleClosePlayer = () => {
        setShowModal(false);
        // Refresh modules to get updated progress
        dispatch(fetchLearnerModulesRequest());
    };

    const handleCompleteStep = () => {
        if (!selectedStep) return;

        const nextStep = selectedStep.id + 1;
        if (nextStep > activeStep && nextStep < steps.length) {
            setActiveStep(nextStep);
            localStorage.setItem(`onb_progress_${currentUser?.id || 'guest'}`, String(nextStep));

            Swal.fire({
                icon: 'success',
                title: 'Félicitations !',
                text: `Vous avez validé l'étape "${selectedStep.title}". L'étape suivante est maintenant disponible !`,
                timer: 2500,
                showConfirmButton: false
            });
        } else if (nextStep === steps.length) {
            if (activeStep < steps.length) {
                setActiveStep(steps.length);
                localStorage.setItem(`onb_progress_${currentUser?.id || 'guest'}`, String(steps.length));
            }
            Swal.fire({
                icon: 'success',
                title: 'Onboarding Complété ! 🎉',
                text: "Vous avez brillamment parcouru l'ensemble des modules d'intégration ! Bienvenue chez Blue Scratch !",
                confirmButtonColor: '#10b981'
            });
        }

        setShowModal(false);
        // Refresh modules immediately on complete to sync dynamic lock states
        dispatch(fetchLearnerModulesRequest());
    };

    const handleResetProgress = async () => {
        const result = await Swal.fire({
            title: 'Réinitialiser la progression ?',
            text: "Êtes-vous sûr de vouloir reprendre votre parcours d'intégration depuis le début ?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Oui, réinitialiser',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#ee645f',
            cancelButtonColor: '#6b7280'
        });

        if (result.isConfirmed) {
            setActiveStep(0);
            localStorage.setItem(`onb_progress_${currentUser?.id || 'guest'}`, '0');
            Swal.fire({
                icon: 'success',
                title: 'Progression réinitialisée',
                text: 'Votre parcours a été remis à zéro.',
                timer: 1500,
                showConfirmButton: false
            });
        }
    };

    const handleLogout = () => {
        sessionStorage.clear();
        dispatch({ type: 'LOGOUT' });
        window.location.href = '/';
    };

    return (
        <div className="onb-portal-wrapper" style={{ height: '100vh' }}>
            {/* Fallback space stars styling if image is missing */}
            <div className="onb-stars" />
            <div className="onb-twinkling" />

            {/* ── Profile Avatar (top-right) ── */}
            <div ref={userDropdownRef} style={{
                position: 'absolute', top: '10px', right: '30px', zIndex: 999,
                display: 'flex', alignItems: 'center'
            }}>
                <div 
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    style={{
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '4px 12px 4px 4px', borderRadius: '50px',
                        background: showModal ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)',
                        border: showModal ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.12)',
                        transition: 'background 0.2s'
                    }}
                >
                    <img 
                        src={profilePhotoSrc} 
                        alt="Profile" 
                        style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                            e.target.onerror = null; 
                            e.target.src = defaultProfileImage;
                        }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: showModal ? '#111827' : '#fff' }}>
                            {currentUser?.fullName || currentUser?.firstName || 'Utilisateur'}
                        </span>
                        <span style={{ fontSize: '11px', color: showModal ? '#6b7280' : 'rgba(255,255,255,0.5)', fontWeight: '500' }}>
                            {selectedRole?.name || currentUser?.jobName || authUser?.jobName || 'Nouvelle recrue'}
                        </span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={showModal ? '#4b5563' : 'white'} style={{ opacity: 0.6 }}>
                        <path d="M6 9l6 6 6-6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                {isUserDropdownOpen && (
                    <div style={{
                        position: 'absolute', top: '54px', right: '0',
                        background: 'rgba(13, 21, 39, 0.96)', backdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255, 255, 255, 0.12)', borderRadius: '12px',
                        padding: '8px', minWidth: '230px',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 1000
                    }}>
                        {/* User name */}
                        <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#fff' }}>
                                {currentUser?.fullName || currentUser?.firstName || 'Utilisateur'}
                            </span>
                            {selectedRole && (
                                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                                    {selectedRole.name}
                                </div>
                            )}
                        </div>

                        {/* Roles */}
                        {userRoles.length > 0 && (
                            <div style={{ padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '4px' }}>
                                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', padding: '4px 12px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>
                                    Changer de rôle
                                </div>
                                {userRoles.map((role) => (
                                    <div key={role.id}
                                        onClick={() => handleRoleChange(role)}
                                        style={{
                                            padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            background: selectedRole?.id === role.id ? 'rgba(238,100,95,0.15)' : 'transparent',
                                            transition: 'background 0.2s', margin: '2px 4px'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = selectedRole?.id === role.id ? 'rgba(238,100,95,0.15)' : 'transparent'}
                                    >
                                        <span style={{ fontSize: '16px' }}>{getRoleIcon(role.name)}</span>
                                        <span style={{ fontSize: '13px', color: '#fff', fontWeight: selectedRole?.id === role.id ? '700' : '500' }}>{role.name}</span>
                                        {selectedRole?.id === role.id && (
                                            <CheckCircle size={14} style={{ marginLeft: 'auto', color: '#ee645f' }} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Logout */}
                        <div 
                            onClick={handleLogout}
                            style={{
                                padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '10px', color: '#ee645f',
                                transition: 'background 0.2s', margin: '2px 4px'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(238,100,95,0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <LogOut size={16} />
                            <span style={{ fontSize: '13px', fontWeight: '600' }}>Se déconnecter</span>
                        </div>
                    </div>
                )}
            </div>



            {/* ── Main Content ── */}
            <main className="onb-main">
                <div className="onb-welcome-header">
                    <h1 className="onb-welcome-title">Bienvenue à bord !</h1>
                    <p className="onb-welcome-subtitle">
                        Ensemble, transformons chaque défi en opportunité pour révolutionner le monde digital.
                    </p>
                    <p className="onb-welcome-subtitle">Nous comptons sur vous !</p>
                </div>

                {/* ── Roadmap Section ── */}
                <div className="onb-roadmap-section">

                    <svg className="onb-wave-svg" viewBox="0 0 1200 320" preserveAspectRatio="none">
                        {steps.map((step, idx) => {
                            if (idx === 0) return null;
                            const count = steps.length;
                            const x1 = 120 + (960 / Math.max(1, count - 1)) * (idx - 1);
                            const x2 = 120 + (960 / Math.max(1, count - 1)) * idx;
                            const y1 = (idx - 1) % 2 === 0 ? 285 : 35;
                            const y2 = idx % 2 === 0 ? 285 : 35;
                            const cx = (x1 + x2) / 2;

                            const isSegmentDone = activeStep > idx;
                            const isSegmentCurrent = activeStep === idx;
                            const stroke = isSegmentDone
                                ? '#10b981'
                                : isSegmentCurrent
                                    ? '#ee645f'
                                    : 'rgba(255,255,255,0.1)';
                            const dasharray = isSegmentCurrent ? '7,6' : 'none';

                            return (
                                <path
                                    key={idx}
                                    d={`M ${x1},${y1} C ${cx},${y1} ${cx},${y2} ${x2},${y2}`}
                                    fill="none"
                                    stroke={stroke}
                                    strokeWidth={isSegmentDone || isSegmentCurrent ? 2.5 : 2}
                                    strokeDasharray={dasharray}
                                    opacity={isSegmentDone ? 0.8 : isSegmentCurrent ? 0.6 : 1}
                                />
                            );
                        })}
                    </svg>


                    {/* Interactive Circles */}
                    <div className="onb-circles-container">
                        {steps.map((step, idx) => {
                            const isCompleted = activeStep > step.id;
                            const isCurrent = activeStep === step.id;
                            const isLocked = activeStep < step.id;

                            const count = steps.length;
                            const leftPos = 10 + (80 / Math.max(1, count - 1)) * idx;
                            const isBottom = idx % 2 === 0;
                            const positionStyle = {
                                left: `${leftPos}%`,
                                top: isBottom ? '89%' : '11%',
                            };

                            let circleClass = 'onb-circle';
                            if (isCompleted) circleClass += ' completed';
                            else if (isCurrent) circleClass += ' current';
                            else if (isLocked) circleClass += ' locked';

                            const nodeClass = `onb-circle-node${isLocked ? ' locked' : ''}`;

                            return (
                                <div
                                    key={step.id}
                                    className="onb-circle-node"
                                    style={positionStyle}
                                >
                                    <div
                                        className={circleClass}
                                        onClick={() => handleStepClick(step)}
                                        title={step.title}
                                    >
                                        {/* anneau pulsant sur l'étape active */}
                                        {isCurrent && <div className="onb-pulse-ring" />}

                                        <div className="onb-circle-inner">
                                            {step.picture ? (
                                                <img
                                                    src={`${API_BASE_URL}/api/modules-cle/images/${step.picture}`}
                                                    alt={step.title}
                                                    className="onb-circle-image"
                                                />
                                            ) : (
                                                <BookOpen className="onb-circle-icon" size={38} />
                                            )}
                                            <div className="onb-circle-title-inside">{step.title}</div>
                                        </div>

                                        {/* overlay cadenas */}
                                        {isLocked && (
                                            <div className="onb-lock-overlay">
                                                <Lock size={22} className="onb-lock-icon" />
                                            </div>
                                        )}

                                        {/* badge check */}
                                        {isCompleted && (
                                            <div className="onb-complete-badge">
                                                <CheckCircle size={12} fill="#10b981" color="white" />
                                            </div>
                                        )}
                                    </div>

                                    {/*<span className="onb-step-num">Étape {String(step.id + 1).padStart(2, '0')}</span>*/}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </main>



            {/* ── Immersive Module Player ── */}
            {showModal && selectedStep && (
                <OnboardingModulePlayer
                    moduleId={selectedStep.moduleId}
                    missionIndex={selectedStep.id}
                    missionTitle={selectedStep.title}
                    onClose={handleClosePlayer}
                    onCompleteStep={handleCompleteStep}
                />
            )}
        </div>
    );
};

export default OnboardingRecrue;
